import { useState, useRef } from "react";
import { FileText, Loader2, Download, RefreshCw, Trash2, ChevronDown } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { extractSyncBolS3Error, formatSyncBolS3Error } from "@/lib/sync-bol-s3-error";
import { deleteOrderDocument } from "@/lib/order-doc-delete";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ZoomableImage } from "@/components/ui/ZoomableImage";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { cn } from "@/lib/utils";
import { DocumentUpload } from "@/components/documents/DocumentUpload";
import type { ExtractionRow } from "@/components/documents/DocumentHistory";
import { StatusBadge } from "@/components/documents/DocumentHistory";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  FileImage, AlertCircle,
} from "lucide-react";

interface TenderViewerDialogProps {
  orderId: string;
  orderUniqueId: string;
}

function TenderDocumentPreview({ filePath }: { filePath: string }) {
  const { data: urls, isLoading: loading } = useQuery({
    queryKey: ["doc-preview-url", "tender-images", filePath],
    queryFn: async () => {
      for (const b of ["tender-images", "idp-uploads"]) {
        const { data, error } = await supabase.storage
          .from(b)
          .createSignedUrl(filePath, 3600);
        if (!error && data?.signedUrl) {
          let blobUrl: string | null = null;
          try {
            const resp = await fetch(data.signedUrl);
            if (resp.ok) {
              const blob = await resp.blob();
              blobUrl = URL.createObjectURL(blob);
              return { signedUrl: data.signedUrl, blobUrl };
            }
          } catch { /* ignore */ }
          return { signedUrl: data.signedUrl, blobUrl };
        }
      }
      return null;
    },
    staleTime: 1000 * 60 * 5,
  });

  const signedUrl = urls?.signedUrl ?? null;
  const blobUrl = urls?.blobUrl ?? null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 bg-muted/30 rounded-lg">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!blobUrl && !signedUrl) {
    return (
      <div className="flex items-center justify-center py-8 bg-muted/30 rounded-lg text-sm text-muted-foreground">
        Unable to load preview
      </div>
    );
  }

  const isPdf = filePath.toLowerCase().endsWith(".pdf");
  const isImage = /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(filePath);

  if (isPdf) {
    const viewUrl = signedUrl || blobUrl;
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 bg-muted/30 rounded-lg border">
        <FileImage className="h-16 w-16 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">PDF preview is not supported in this view</p>
        <div className="flex gap-2">
          <Button variant="default" size="sm" asChild>
            <a href={viewUrl!} target="_blank" rel="noopener noreferrer">
              <FileText className="h-4 w-4 mr-2" /> View PDF
            </a>
          </Button>
          {blobUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={blobUrl} download={filePath.split("/").pop()}>
                <Download className="h-4 w-4 mr-2" /> Download
              </a>
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (isImage && blobUrl) {
    return (
      <ZoomableImage
        src={blobUrl}
        openUrl={signedUrl || blobUrl}
        downloadUrl={blobUrl || signedUrl || undefined}
        fileName={filePath.split("/").pop() || "document"}
        alt="Document preview"
        className="rounded-lg border bg-muted/30 h-[50vh]"
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-8 gap-3 bg-muted/30 rounded-lg">
      <FileText className="h-12 w-12 text-muted-foreground" />
      {blobUrl && (
        <Button asChild variant="outline" size="sm">
          <a href={blobUrl} download>
            <Download className="h-4 w-4 mr-2" /> Download to View
          </a>
        </Button>
      )}
    </div>
  );
}

const TENDER_LABELS: Record<string, string> = {
  order_number: "Route / Order Number",
  origin_timestamp: "Origin Timestamp",
  destination_timestamp: "Destination Timestamp",
  origin_location: "Origin Location",
  destination_location: "Destination Location",
  trailer_number: "Trailer Number",
  flat_rate: "Flat Rate",
  fuel_surcharge: "Fuel Surcharge",
  total_rate: "Total Rate",
  carrier_name: "Carrier",
  shipper_name: "Shipper",
  commodity: "Commodity",
  weight: "Weight",
  special_instructions: "Special Instructions",
};

function TenderDocumentActions({
  tenderId,
  orderId,
  orderUniqueId,
  filePath,
}: {
  tenderId: string;
  orderId: string;
  orderUniqueId: string;
  filePath?: string | null;
}) {
  const queryClient = useQueryClient();
  const { activeTenant, tenantId } = useTenant();
  const fileRef = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["tender-images", orderId] });
    queryClient.invalidateQueries({ queryKey: ["tender-images-count", orderId] });
    queryClient.invalidateQueries({ queryKey: ["tender-extractions", orderId] });
  };

  const handleReplace = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplacing(true);
    let s3KeyForRollback: string | null = null;
    let newStoragePath: string | null = null;
    try {
      if (!tenantId) throw new Error("Tenant context is not ready");

      // Step 1: Sync-then-Persist — push the replacement tender to S3 first.
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1] || result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data: syncResult, error: syncError } = await supabase.functions.invoke("sync-bol-s3", {
        body: {
          file_data: base64,
          file_name: file.name,
          tenant_slug: activeTenant,
          order_id: orderUniqueId,
          doc_kind: "tender",
        },
      });
      if (syncError || !syncResult?.success) {
        const detail = await extractSyncBolS3Error(syncError, syncResult);
        throw new Error(`S3 upload failed: ${formatSyncBolS3Error(detail)}`);
      }
      s3KeyForRollback = syncResult?.s3_key ?? null;

      // Step 2: Upload to local storage
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `tender/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage.from("tender-images").upload(path, file);
      if (upErr) throw upErr;
      newStoragePath = path;

      // Step 3: Update DB row (tenant-scoped)
      const { error: updErr } = await supabase
        .from("order_tender_images")
        .update({
          image_path: path,
          image_url: path,
          s3_key: s3KeyForRollback,
          file_name: file.name,
        } as never)
        .eq("id", tenderId)
        .eq("tenant_id", tenantId);
      if (updErr) throw updErr;

      // Step 4: Strict cleanup of OLD S3 object(s) for the previous file_path.
      if (filePath) {
        const fileName = filePath.split("/").pop()?.replace(/^\d+-/, "") || "";
        const sourcePaths = Array.from(new Set([filePath, fileName].filter(Boolean)));
        const { data: syncRows } = await supabase
          .from("s3_sync_log")
          .select("s3_key")
          .eq("order_id", orderUniqueId)
          .eq("status", "synced")
          .in("source_path", sourcePaths.length > 0 ? sourcePaths : ["__none__"]);
        const oldKeys = Array.from(new Set((syncRows ?? []).map((r: any) => r.s3_key as string)))
          .filter((k) => k !== s3KeyForRollback); // never delete the just-uploaded object
        for (const key of oldKeys) {
          const { data, error } = await supabase.functions.invoke("sync-bol-s3", {
            body: {
              action: "delete",
              tenant_slug: activeTenant,
              order_id: orderUniqueId,
              s3_key: key,
            },
          });
          if (error || (data && data.success === false)) {
            throw new Error(error?.message || data?.error || `S3 delete failed for old key ${key}`);
          }
        }
        // Best-effort: drop the previous object from local storage too.
        await supabase.storage.from("tender-images").remove([filePath]).catch(() => undefined);

        // Best-effort: drop stale document_extractions row(s) for the previous file.
        try {
          const orConditions: string[] = [];
          if (filePath) orConditions.push(`file_path.eq.${filePath}`);
          if (fileName) orConditions.push(`file_name.eq.${fileName}`);
          if (orConditions.length > 0) {
            await supabase
              .from("document_extractions")
              .delete()
              .eq("tenant_id", tenantId)
              .or(orConditions.join(","));
          }
        } catch (extractionErr) {
          console.warn("Failed to clean prior tender document_extractions row", extractionErr);
        }
      }

      toast.success("File replaced");
      invalidate();
    } catch (err: any) {
      // Rollback: undo the new S3 object + the new local storage object so we
      // don't leave orphans when the local persist fails.
      if (s3KeyForRollback) {
        try {
          await supabase.functions.invoke("sync-bol-s3", {
            body: {
              action: "delete",
              tenant_slug: activeTenant,
              order_id: orderUniqueId,
              s3_key: s3KeyForRollback,
            },
          });
        } catch (cleanupErr) {
          console.error("S3 rollback failed for key", s3KeyForRollback, cleanupErr);
          toast.warning(
            `S3 cleanup failed — manual review needed for s3_key=${s3KeyForRollback}`
          );
        }
      }
      if (newStoragePath) {
        await supabase.storage.from("tender-images").remove([newStoragePath]).catch(() => undefined);
      }
      toast.error("Replace failed: " + (err.message || "Unknown error"));
    } finally {
      setReplacing(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (!tenantId) throw new Error("Tenant context is not ready");
      await deleteOrderDocument({
        table: "order_tender_images",
        bucket: "tender-images",
        rowId: tenderId,
        imagePath: filePath,
        orderUniqueId,
        tenantId,
        tenantSlug: activeTenant,
      });
      toast.success("Document removed from order");
      invalidate();
    } catch (err: any) {
      toast.error("Delete failed: " + (err.message || "Unknown error"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf" onChange={handleReplace} />
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={replacing}
        onClick={() => fileRef.current?.click()}
      >
        {replacing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        Replace
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive"
            disabled={deleting}
          >
            {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TenderExistingView({ orderId, orderUniqueId }: { orderId: string; orderUniqueId: string }) {
  const { data: tenderImages, isLoading } = useQuery({
    queryKey: ["tender-images", orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_tender_images")
        .select("*")
        .eq("order_id", orderId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const tenderPaths = (tenderImages || []).map((t) => t.image_path).filter(Boolean) as string[];
  const tenderFileNames = tenderPaths.map((p) => p.split("/").pop()?.replace(/^\d+-/, "") || "").filter(Boolean);

  const { data: extractions } = useQuery({
    queryKey: ["tender-extractions", orderId, tenderPaths, tenderFileNames],
    queryFn: async () => {
      if (tenderPaths.length === 0) return [];
      const conditions: string[] = [];
      if (tenderPaths.length > 0) conditions.push(`file_path.in.(${tenderPaths.join(",")})`);
      if (tenderFileNames.length > 0) conditions.push(`file_name.in.(${tenderFileNames.join(",")})`);
      const { data, error } = await supabase
        .from("document_extractions")
        .select("*")
        .or(conditions.join(","));
      if (error) throw error;
      return (data || []) as ExtractionRow[];
    },
    enabled: tenderPaths.length > 0,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const extractionMap = new Map<string, ExtractionRow>();
  (extractions || []).forEach((e) => {
    extractionMap.set(e.file_path, e);
    if (e.file_name) extractionMap.set(e.file_name, e);
  });

  const findExtraction = (imagePath: string | null): ExtractionRow | null => {
    if (!imagePath) return null;
    const direct = extractionMap.get(imagePath);
    if (direct) return direct;
    const fileName = imagePath.split("/").pop()?.replace(/^\d+-/, "") || "";
    return extractionMap.get(fileName) || null;
  };

  return (
    <div className="space-y-4">
      {(tenderImages || []).map((tender) => {
        const extraction = findExtraction(tender.image_path);

        if (extraction) {
          const displayData = extraction.verified_data || extraction.extracted_data || {};
          const confidence = (displayData as any).confidence_score ?? 0;

          return (
            <div key={tender.id} className="space-y-4">
              <p className="text-xs font-medium text-foreground truncate">{extraction.file_name}</p>
              <div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground">
                <StatusBadge status={extraction.status} isVerified={extraction.is_verified} isAttached />
                <span className="ml-auto">Uploaded {format(new Date(extraction.created_at), "MMM d, yyyy h:mm a")}</span>
              </div>

              {extraction.file_path && (
                <div className="relative">
                  <TenderDocumentPreview filePath={extraction.file_path} />
                  <div className="absolute top-2 right-2 z-10">
                    <TenderDocumentActions
                      tenderId={tender.id}
                      orderId={orderId}
                      orderUniqueId={orderUniqueId}
                      filePath={tender.image_path}
                    />
                  </div>
                </div>
              )}

              {extraction.error_message && (
                <div className="bg-destructive/10 text-destructive text-sm rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  {extraction.error_message}
                </div>
              )}

              {false && Object.keys(displayData).length > 0 && (
                <Collapsible>
                  <div className="border rounded-lg p-4">
                    <CollapsibleTrigger className="flex w-full items-center justify-between">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Extracted Data
                      </h4>
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <dl className="space-y-2 mt-3">
                        {Object.entries(TENDER_LABELS).map(([key, label]) => {
                          const val = (displayData as any)[key];
                          if (val === null || val === undefined || val === "") return null;
                          return (
                            <div key={key} className="flex justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
                              <dt className="text-sm text-muted-foreground shrink-0">{label}</dt>
                              <dd className="text-sm font-medium text-foreground text-right">{String(val)}</dd>
                            </div>
                          );
                        })}
                        {confidence > 0 && (
                          <div className="flex justify-between gap-4 py-1.5">
                            <dt className="text-sm text-muted-foreground">Confidence</dt>
                            <dd className={cn(
                              "text-sm font-semibold",
                              confidence >= 0.9 ? "text-primary" : confidence >= 0.7 ? "text-amber-600" : "text-destructive"
                            )}>
                              {Math.round(confidence * 100)}%
                            </dd>
                          </div>
                        )}
                      </dl>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}
            </div>
          );
        }

        // No extraction — show preview with status chips
        return (
          <div key={tender.id} className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground">
              <Badge variant="outline" className="gap-1">
                <FileText className="h-3 w-3" /> Tender
              </Badge>
              <StatusBadge status="completed" isVerified={false} isAttached />
              <span className="ml-auto">Uploaded {format(new Date(tender.created_at), "MMM d, yyyy h:mm a")}</span>
            </div>

            <div className="relative">
              {tender.image_path ? (
                <TenderDocumentPreview filePath={tender.image_path} />
              ) : tender.image_url ? (
                <div className="rounded-lg overflow-hidden border bg-muted/30">
                  <img src={tender.image_url} alt="Tender" className="w-full h-auto max-h-[50vh] object-contain mx-auto" />
                </div>
              ) : null}
              <div className="absolute top-2 right-2 z-10">
                <TenderDocumentActions
                  tenderId={tender.id}
                  orderId={orderId}
                  orderUniqueId={orderUniqueId}
                  filePath={tender.image_path}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TenderViewerDialog({ orderId, orderUniqueId }: TenderViewerDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: tenderCount } = useQuery({
    queryKey: ["tender-images-count", orderId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("order_tender_images")
        .select("id", { count: "exact", head: true })
        .eq("order_id", orderId);

      if (error) throw error;
      return count || 0;
    },
  });

  const hasTenderImages = (tenderCount ?? 0) > 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <FileText
                className={cn(
                  "h-4 w-4",
                  hasTenderImages ? "text-foreground" : "text-muted-foreground/40"
                )}
              />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {hasTenderImages ? "View Tender" : "No Tender"}
        </TooltipContent>
      </Tooltip>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pr-8">
          <DialogTitle>
            {hasTenderImages ? `Tender for ${orderUniqueId}` : `Add Tender to ${orderUniqueId}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto pt-2">
          {hasTenderImages ? (
            <TenderExistingView orderId={orderId} orderUniqueId={orderUniqueId} />
          ) : (
            <DocumentUpload
              compact
              orderId={orderId}
              orderUniqueId={orderUniqueId}
              documentKind="tender"
              onComplete={async () => {
                await queryClient.invalidateQueries({ queryKey: ["tender-images", orderId] });
                await queryClient.invalidateQueries({ queryKey: ["tender-images-count", orderId] });
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
